import { useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import {
  LocationPinIcon,
  RouteMap,
  RouteStop,
  type RouteMapPoint,
} from "@/components/common/RouteMap";
import {
  getDropoffStops,
  getPickupStops,
  getStopPinColor,
  PIN_COLORS,
  stopDisplayLabel,
  stopToMapPoint,
} from "@/utils/rideRouteStops";
import type { RideApiStop, RideDetails } from "@/types/rideDetails";

const MAP_STOP_TYPES = new Set(["START", "PICKUP", "ENROUTE", "DROP", "END"]);

function StopListItem({ stop }: { stop: RideApiStop }) {
  const title = stopDisplayLabel(stop);
  const subtitle = stop.sc && stop.sc !== title ? stop.sc : undefined;
  const pinColor = getStopPinColor(stop);

  return (
    <li className="flex items-start gap-2 text-sm">
      <LocationPinIcon color={pinColor} className="mt-0.5 h-5 w-3.5" />
      <div className="min-w-0">
        <p className="font-medium text-foreground">{title}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </li>
  );
}

function ViewStopsToggle({
  label,
  expanded,
  onToggle,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="shrink-0 text-xs font-semibold text-primary hover:underline"
    >
      {expanded ? "Hide" : label}
    </button>
  );
}

function MapLegend() {
  const items = [
    { color: PIN_COLORS.start, label: "Start" },
    { color: PIN_COLORS.pickup, label: "Pickup" },
    { color: PIN_COLORS.enroute, label: "En route" },
    { color: PIN_COLORS.drop, label: "Drop-off" },
    { color: PIN_COLORS.end, label: "End" },
  ] as const;

  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
      {items.map(({ color, label }) => (
        <span
          key={label}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          {label}
        </span>
      ))}
    </div>
  );
}

export function RideRouteSection({ ride }: { ride: RideDetails }) {
  const { route } = ride;
  const stops = ride.raw?.route?.stops ?? [];

  const [showPickups, setShowPickups] = useState(false);
  const [showDropoffs, setShowDropoffs] = useState(false);

  const pickupStops = useMemo(() => getPickupStops(stops), [stops]);
  const dropoffStops = useMemo(() => getDropoffStops(stops), [stops]);

  const mapPoints = useMemo(() => {
    const points: RouteMapPoint[] = [];
    const seen = new Set<string>();

    const addPoint = (point: RouteMapPoint | null) => {
      if (!point) return;
      const key = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
      if (seen.has(key)) return;
      seen.add(key);
      points.push(point);
    };

    stops.forEach((stop) => {
      const type = stop.stoptype?.toUpperCase() ?? "";
      if (!MAP_STOP_TYPES.has(type)) return;
      addPoint(stopToMapPoint(stop));
    });

    if (points.length === 0) {
      if (
        typeof route.pickup.lat === "number" &&
        typeof route.pickup.lng === "number"
      ) {
        addPoint({
          lat: route.pickup.lat,
          lng: route.pickup.lng,
          label: route.pickup.label ?? "Start Location",
          color: PIN_COLORS.start,
        });
      }
      if (
        typeof route.drop.lat === "number" &&
        typeof route.drop.lng === "number"
      ) {
        addPoint({
          lat: route.drop.lat,
          lng: route.drop.lng,
          label: route.drop.label ?? "End Location",
          color: PIN_COLORS.end,
        });
      }
    }

    return points;
  }, [stops, route.pickup, route.drop]);

  const hasPickupList = pickupStops.length > 0;
  const hasDropoffList = dropoffStops.length > 0;

  return (
    <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <MapPin className="h-4 w-4 text-emerald-600" aria-hidden />
        Route
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <RouteStop
            variant="start"
            label="Start Location"
            address={route.pickup.address}
            action={
              hasPickupList ? (
                <ViewStopsToggle
                  label="View pickups"
                  expanded={showPickups}
                  onToggle={() => setShowPickups((v) => !v)}
                />
              ) : undefined
            }
          />
          {showPickups && hasPickupList && (
            <ul className="ml-2 mt-3 space-y-2 border-l-2 border-blue-200 pl-6">
              {pickupStops.map((stop) => (
                <StopListItem key={stop.stopid ?? stop.sl} stop={stop} />
              ))}
            </ul>
          )}
        </div>

        <div>
          <RouteStop
            variant="end"
            label="End Location"
            address={route.drop.address}
            action={
              hasDropoffList ? (
                <ViewStopsToggle
                  label="View drop-offs"
                  expanded={showDropoffs}
                  onToggle={() => setShowDropoffs((v) => !v)}
                />
              ) : undefined
            }
          />
          {showDropoffs && hasDropoffList && (
            <ul className="ml-2 mt-3 space-y-2 border-l-2 border-orange-200 pl-6">
              {dropoffStops.map((stop) => (
                <StopListItem key={stop.stopid ?? stop.sl} stop={stop} />
              ))}
            </ul>
          )}
        </div>
      </div>

      <RouteMap mapPoints={mapPoints} expandable className="mt-4" />
      {mapPoints.length > 1 && <MapLegend />}
    </section>
  );
}
